import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAuth } from '../context/AuthContext';
import type { KnowledgeQuizQuestion } from '../knowledgeQuiz';

const emptyChoices = () => ['', '', '', ''];

export default function QuizQuestionAdmin() {
  const { quizQuestions, quizBankReady, saveQuizQuestion, deleteQuizQuestion, publishGeneratedQuiz } = useAuth();
  const [editing, setEditing] = useState<KnowledgeQuizQuestion | null>(null);
  const [topic, setTopic] = useState('');
  const [prompt, setPrompt] = useState('');
  const [choices, setChoices] = useState<string[]>(emptyChoices());
  const [correctIndex, setCorrectIndex] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeQuizQuestion | null>(null);

  const openNew = () => {
    setEditing({ id: `quiz-${Date.now()}`, topic: '', prompt: '', choices: emptyChoices(), answer: '' });
    setTopic('');
    setPrompt('');
    setChoices(emptyChoices());
    setCorrectIndex(0);
    setFormError(null);
  };

  const openEdit = (question: KnowledgeQuizQuestion) => {
    const nextChoices = [...question.choices, '', '', '', ''].slice(0, 4);
    setEditing(question);
    setTopic(question.topic);
    setPrompt(question.prompt);
    setChoices(nextChoices);
    setCorrectIndex(Math.max(0, nextChoices.indexOf(question.answer)));
    setFormError(null);
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
    setFormError(null);
  };

  const save = async () => {
    if (!editing) return;
    const cleanChoices = choices.map((choice) => choice.trim());
    if (!topic.trim() || !prompt.trim()) {
      setFormError('Enter a topic and a question.');
      return;
    }
    if (cleanChoices.some((choice) => !choice)) {
      setFormError('Enter all 4 answers.');
      return;
    }
    if (new Set(cleanChoices.map((choice) => choice.toLowerCase())).size !== 4) {
      setFormError('The 4 answers must be different.');
      return;
    }
    setSaving(true);
    const res = await saveQuizQuestion({
      id: editing.id,
      topic: topic.trim(),
      prompt: prompt.trim(),
      choices: cleanChoices,
      answer: cleanChoices[correctIndex],
    });
    setSaving(false);
    if (!res.success) {
      setFormError(res.error || 'Could not save this question.');
      return;
    }
    setStatus({ type: 'success', text: 'Quiz question saved. Drivers will see these answers.' });
    setEditing(null);
  };

  const loadGenerated = async () => {
    setSaving(true);
    const res = await publishGeneratedQuiz();
    setSaving(false);
    if (!res.success) {
      setStatus({ type: 'error', text: res.error || 'Could not load the current quiz.' });
      return;
    }
    setStatus({
      type: 'success',
      text: `Loaded ${res.count || 0} questions. Edit any answer that does not read the way you want.`,
    });
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    const res = await deleteQuizQuestion(deleteTarget.id);
    setSaving(false);
    if (!res.success) {
      setStatus({ type: 'error', text: res.error || 'Could not remove that question.' });
      return;
    }
    setStatus({ type: 'success', text: 'Question removed.' });
    setDeleteTarget(null);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Quiz Questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {quizBankReady
              ? 'Drivers take these questions. Each one has 4 answers, and the selected answer is the correct one.'
              : 'The quiz is still written automatically. Load it here, then change the questions and the 4 answers. Adding a question on its own replaces that automatic quiz.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!quizBankReady && (
            <Button variant="outlined" disabled={saving} onClick={loadGenerated}>
              Load current quiz
            </Button>
          )}
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openNew}>
            Add question
          </Button>
        </Box>
      </Box>

      {status && (
        <Typography color={status.type === 'error' ? 'error' : 'success.main'} sx={{ mb: 2 }}>
          {status.text}
        </Typography>
      )}

      {quizBankReady && quizQuestions.length === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography color="text.secondary">No quiz questions yet. Add one to publish the quiz.</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {quizQuestions.map((question, index) => (
          <Paper key={question.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="primary" fontWeight={700}>
                  Question {index + 1}
                  {question.topic ? ` · ${question.topic}` : ''}
                </Typography>
                <Typography fontWeight={700} sx={{ mt: 0.5 }}>
                  {question.prompt}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexShrink: 0 }}>
                <IconButton aria-label="Edit question" onClick={() => openEdit(question)}>
                  <EditOutlinedIcon />
                </IconButton>
                <IconButton aria-label="Remove question" color="error" onClick={() => setDeleteTarget(question)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1.5 }}>
              {question.choices.map((choice) => (
                <Chip
                  key={choice}
                  label={choice}
                  color={choice === question.answer ? 'success' : 'default'}
                  variant={choice === question.answer ? 'filled' : 'outlined'}
                  sx={{ height: 'auto', justifyContent: 'flex-start', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 } }}
                />
              ))}
            </Box>
          </Paper>
        ))}
      </Box>

      <Dialog open={Boolean(editing)} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>{editing && quizQuestions.some((item) => item.id === editing.id) ? 'Edit question' : 'Add question'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Topic" value={topic} onChange={(event) => setTopic(event.target.value)} fullWidth sx={{ mt: 1 }} />
          <TextField
            label="Question"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <Typography variant="body2" color="text.secondary">
            Enter 4 answers and select the correct one.
          </Typography>
          {choices.map((choice, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Radio
                checked={correctIndex === index}
                onChange={() => setCorrectIndex(index)}
                inputProps={{ 'aria-label': `Mark answer ${index + 1} correct` }}
                sx={{ mt: 1 }}
              />
              <TextField
                label={`Answer ${index + 1}`}
                value={choice}
                onChange={(event) =>
                  setChoices((current) => current.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)))
                }
                fullWidth
                multiline
              />
            </Box>
          ))}
          {formError && <Typography color="error">{formError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save question'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !saving && setDeleteTarget(null)}>
        <DialogTitle>Remove question</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteTarget ? `Remove “${deleteTarget.prompt}” from the quiz?` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={remove} disabled={saving}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
